import { lazy } from 'react';

/**
 * Wraps React.lazy with a one-time chunk-reload recovery.
 *
 * When a hashed chunk filename changes after a Netlify deploy, clients
 * holding a stale `index.html` reference the old chunk URL. The browser
 * 404s the chunk and React Router renders its default
 * "Unexpected Application Error! Loading chunk N failed" UI.
 *
 * This helper detects that failure and reloads the page exactly once so
 * the browser fetches the latest `index.html` + chunk manifest. A
 * sessionStorage flag prevents reload loops if the reload itself still
 * fails (genuine outage / offline).
 *
 * @param {() => Promise<{ default: any }>} factory - dynamic import factory
 * @param {string} chunkName - optional unique key per chunk (defaults to factory string)
 */
const lazyWithRetry = (factory, chunkName) => {
  const STORAGE_KEY = `chunkReload:${chunkName || factory.toString()}`;

  return lazy(async () => {
    try {
      const component = await factory();
      // Successful load — clear any prior reload flag for this chunk.
      try {
        window.sessionStorage.removeItem(STORAGE_KEY);
      } catch (_) {
        // sessionStorage may be unavailable (private mode, SSR); ignore.
      }
      return component;
    } catch (error) {
      const message = (error && (error.message || error.toString())) || '';
      const isChunkLoadError =
        error?.name === 'ChunkLoadError' ||
        /Loading chunk [\d]+ failed/i.test(message) ||
        /Loading CSS chunk [\d]+ failed/i.test(message) ||
        /Failed to fetch dynamically imported module/i.test(message);

      if (!isChunkLoadError) {
        throw error;
      }

      // Check if we've already attempted a reload for this chunk in this session.
      let alreadyReloaded = false;
      try {
        alreadyReloaded = window.sessionStorage.getItem(STORAGE_KEY) === '1';
      } catch (_) {
        // No sessionStorage access — fall through to re-throw.
      }

      if (alreadyReloaded) {
        // Reload already attempted; surface the error to the route error
        // boundary so the user sees a friendly fallback rather than an
        // endless reload loop.
        throw error;
      }

      try {
        window.sessionStorage.setItem(STORAGE_KEY, '1');
      } catch (_) {
        // ignore
      }

      // Hard reload — pulls the freshest index.html + chunk manifest.
      window.location.reload();

      // Return a never-resolving promise so React doesn't try to render
      // anything before the reload happens.
      return new Promise(() => {});
    }
  });
};

export default lazyWithRetry;
